import { useEffect, useState, useRef } from "react";
import axios from "axios";
import { toast } from "react-toastify";
import { confirmAlert } from "react-confirm-alert";
import "react-confirm-alert/src/react-confirm-alert.css";
import { FaRegTrashAlt, FaRegEdit } from "react-icons/fa";
import { MdAdd, MdClose, MdCheck, MdKeyboardArrowUp, MdKeyboardArrowDown } from "react-icons/md";
import { ImSpinner2 } from "react-icons/im";
import { FiImage, FiBookOpen } from "react-icons/fi";
import PanelSkeleton from "../PanelSkeleton";
import ProductImage from "../ProductImage";
import mediaUrl from "../../utils/mediaUrl";
import store from "../../redux/store";
import styles from "../../styles/categoriesPanel.module.css";
import storyStyles from "../../styles/storiesPanel.module.css";

const authConfig = (extra = {}) => {
  const token = store.getState().auth?.token;
  return {
    withCredentials: true,
    ...extra,
    headers: { ...(token ? { Authorization: `Bearer ${token}` } : {}), ...extra.headers },
  };
};

const API = `${process.env.REACT_APP_BACKEND_URL}/api/stories`;
const MAX_IMAGES = 10;
const MAX_IMAGE_SIZE = 5 * 1024 * 1024;

const DEFAULT_FORM = {
  title: "",
  description: "",
  wallSize: "",
  paintingSize: "",
  customerName: "",
  country: "",
};

const TEXT_FIELDS = [
  { name: "wallSize",     label: "مقاس الحائط",  placeholder: "مثال: 300 × 250 سم" },
  { name: "paintingSize", label: "مقاس اللوحة", placeholder: "مثال: 120 × 60 سم" },
  { name: "customerName", label: "اسم العميل",  placeholder: "مثال: أحمد محمد" },
  { name: "country",      label: "البلد",       placeholder: "مثال: فلسطين" },
];

const StoriesPanel = () => {
  const [stories, setStories]       = useState([]);
  const [loading, setLoading]       = useState(true);
  const [saving, setSaving]         = useState(false);
  const [deleting, setDeleting]     = useState(false);
  const [reordering, setReordering] = useState(false);
  const [editId, setEditId]         = useState(null);
  const [form, setForm]             = useState(DEFAULT_FORM);
  const [existingImages, setExistingImages] = useState([]); // [{ id, imageUrl }]
  const [removedIds, setRemovedIds] = useState([]);
  const [newImages, setNewImages]   = useState([]);          // [{ file, preview }]
  const [showForm, setShowForm]     = useState(false);
  const titleRef = useRef(null);
  const imgRef   = useRef(null);

  const fetchStories = async () => {
    try {
      const res = await axios.get(`${API}/dashboardFetch`, authConfig());
      setStories(res.data?.data ?? []);
    } catch {
      toast.error(<span className="toast">فشل تحميل القصص</span>);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { fetchStories(); }, []); // eslint-disable-line react-hooks/exhaustive-deps

  const resetForm = () => {
    newImages.forEach((img) => URL.revokeObjectURL(img.preview));
    setEditId(null);
    setForm(DEFAULT_FORM);
    setExistingImages([]);
    setRemovedIds([]);
    setNewImages([]);
  };

  const openAdd = () => {
    resetForm();
    setShowForm(true);
    setTimeout(() => titleRef.current?.focus(), 80);
  };

  const openEdit = (story) => {
    resetForm();
    setEditId(story.id);
    setForm({
      title:        story.title ?? "",
      description:  story.description ?? "",
      wallSize:     story.wallSize ?? "",
      paintingSize: story.paintingSize ?? "",
      customerName: story.customerName ?? "",
      country:      story.country ?? "",
    });
    setExistingImages(story.images ?? []);
    setShowForm(true);
    window.scrollTo({ top: 0, behavior: "smooth" });
    setTimeout(() => titleRef.current?.focus(), 80);
  };

  const closeForm = () => {
    resetForm();
    setShowForm(false);
  };

  const handleChange = (e) => {
    const { name, value } = e.target;
    setForm((f) => ({ ...f, [name]: value }));
  };

  const totalImages = existingImages.length + newImages.length;

  const handleImagesChange = (e) => {
    const files = Array.from(e.target.files ?? []);
    e.target.value = "";
    if (!files.length) return;

    const room = MAX_IMAGES - totalImages;
    if (files.length > room) {
      toast.error(<span className="toast">الحد الأقصى {MAX_IMAGES} صور لكل قصة</span>);
    }

    const accepted = files.slice(0, Math.max(room, 0)).filter((file) => {
      if (file.size > MAX_IMAGE_SIZE) {
        toast.error(<span className="toast">حجم الصورة {file.name} يجب أن يكون أقل من 5 ميجابايت</span>);
        return false;
      }
      return true;
    });

    setNewImages((prev) => [
      ...prev,
      ...accepted.map((file) => ({ file, preview: URL.createObjectURL(file) })),
    ]);
  };

  const removeExistingImage = (id) => {
    setExistingImages((prev) => prev.filter((img) => img.id !== id));
    setRemovedIds((prev) => [...prev, id]);
  };

  const removeNewImage = (index) => {
    setNewImages((prev) => {
      URL.revokeObjectURL(prev[index].preview);
      return prev.filter((_, i) => i !== index);
    });
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!form.title.trim()) return;
    setSaving(true);
    try {
      const fd = new FormData();
      Object.entries(form).forEach(([key, value]) => fd.append(key, value.trim()));
      newImages.forEach((img) => fd.append("images", img.file));

      if (editId) {
        if (removedIds.length) fd.append("removeImageIds", removedIds.join(","));
        await axios.put(`${API}/${editId}`, fd, authConfig());
        toast.success(<span className="toast">تم تعديل القصة</span>);
      } else {
        await axios.post(API, fd, authConfig());
        toast.success(<span className="toast">تم إضافة القصة</span>);
      }
      closeForm();
      fetchStories();
    } catch {
      toast.error(<span className="toast">فشلت العملية. حاول مرة أخرى</span>);
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = (story) => {
    confirmAlert({
      message: `حذف قصة "${story.title}" ؟`,
      buttons: [
        {
          label: "نعم",
          onClick: async () => {
            setDeleting(true);
            try {
              await axios.delete(`${API}/${story.id}`, authConfig());
              toast.success(<span className="toast">تم حذف القصة</span>);
              if (editId === story.id) closeForm();
              fetchStories();
            } catch {
              toast.error(<span className="toast">فشل الحذف. حاول مرة أخرى</span>);
            } finally {
              setDeleting(false);
            }
          },
        },
        { label: "لا" },
      ],
    });
  };

  const move = async (index, direction) => {
    const next = [...stories];
    const swapIdx = direction === "up" ? index - 1 : index + 1;
    if (swapIdx < 0 || swapIdx >= next.length) return;

    [next[index], next[swapIdx]] = [next[swapIdx], next[index]];
    setStories(next);

    setReordering(true);
    try {
      await axios.put(
        `${API}/reorder/batch`,
        { items: next.map((story, i) => ({ id: story.id, sortOrder: i })) },
        authConfig()
      );
    } catch {
      toast.error(<span className="toast">فشل حفظ الترتيب. حاول مرة أخرى</span>);
      fetchStories();
    } finally {
      setReordering(false);
    }
  };

  return (
    <div className={styles.panel}>

      {/* ── Header ── */}
      <div className={styles.panelHead}>
        <span className={styles.panelTitle}>
          <FiBookOpen className={styles.panelTitleIcon} />
          قصص تصاميم خاصة
        </span>
        <button className={styles.addBtn} onClick={openAdd} disabled={showForm}>
          <MdAdd /> إضافة قصة
        </button>
      </div>

      {/* ── Add / Edit Form ── */}
      {showForm && (
        <form className={styles.form} onSubmit={handleSubmit} encType="multipart/form-data">
          <div className={styles.formHead}>
            <span className={styles.formTitle}>{editId ? "تعديل القصة" : "قصة جديدة"}</span>
            <button type="button" className={styles.formClose} onClick={closeForm}>
              <MdClose />
            </button>
          </div>

          <div className={styles.formRow}>
            <label className={styles.formLabel}>العنوان *</label>
            <input
              ref={titleRef}
              name="title"
              className={styles.formInput}
              placeholder="مثال: هدية زواج مميزة"
              maxLength={150}
              value={form.title}
              onChange={handleChange}
              required
            />
          </div>

          <div className={styles.formRow}>
            <label className={styles.formLabel}>الوصف</label>
            <textarea
              name="description"
              className={`${styles.formInput} ${storyStyles.textarea}`}
              placeholder="اكتب قصة التصميم..."
              maxLength={5000}
              rows={4}
              value={form.description}
              onChange={handleChange}
            />
          </div>

          <div className={storyStyles.grid}>
            {TEXT_FIELDS.map((field) => (
              <div key={field.name} className={styles.formRow}>
                <label className={styles.formLabel}>{field.label}</label>
                <input
                  name={field.name}
                  className={styles.formInput}
                  placeholder={field.placeholder}
                  maxLength={100}
                  value={form[field.name]}
                  onChange={handleChange}
                />
              </div>
            ))}
          </div>

          <div className={styles.formRow}>
            <label className={styles.formLabel}>
              الصور ({totalImages}/{MAX_IMAGES}) — الصورة الأولى تظهر كغلاف
            </label>

            <div className={storyStyles.images}>
              {existingImages.map((img) => (
                <div key={`e-${img.id}`} className={styles.imgPreviewWrap}>
                  <ProductImage url={mediaUrl(img.imageUrl)} alt="" className={styles.imgPreview} />
                  <button
                    type="button"
                    className={styles.imgRemoveBtn}
                    onClick={() => removeExistingImage(img.id)}
                    title="إزالة الصورة"
                  >
                    <MdClose />
                  </button>
                </div>
              ))}

              {newImages.map((img, i) => (
                <div key={img.preview} className={styles.imgPreviewWrap}>
                  <img src={img.preview} alt="" className={styles.imgPreview} />
                  <button
                    type="button"
                    className={styles.imgRemoveBtn}
                    onClick={() => removeNewImage(i)}
                    title="إزالة الصورة"
                  >
                    <MdClose />
                  </button>
                </div>
              ))}

              {totalImages < MAX_IMAGES && (
                <button
                  type="button"
                  className={`${styles.imgPickBtn} ${storyStyles.addImgBtn}`}
                  onClick={() => imgRef.current?.click()}
                >
                  <FiImage className={styles.imgPickIcon} />
                  إضافة صور
                </button>
              )}
            </div>

            <input
              type="file"
              accept="image/*"
              multiple
              ref={imgRef}
              onChange={handleImagesChange}
              style={{ display: "none" }}
            />
          </div>

          <div className={styles.formActions}>
            <button type="submit" className={styles.saveBtn} disabled={saving}>
              {saving ? <ImSpinner2 className="smallspinner" /> : <><MdCheck /> حفظ</>}
            </button>
            <button type="button" className={styles.cancelFormBtn} onClick={closeForm}>
              إلغاء
            </button>
          </div>
        </form>
      )}

      {/* ── Stories List ── */}
      {loading ? (
        <PanelSkeleton count={3} />
      ) : stories.length === 0 ? (
        <p className="warn">لا توجد قصص بعد. أضف أول قصة!</p>
      ) : (
        <div className={styles.list}>
          {stories.map((story, i) => (
            <div key={story.id} className={styles.row} style={{ animationDelay: `${i * 0.04}s` }}>

              {/* Order controls */}
              <div className={styles.orderBtns}>
                <button
                  className={styles.orderBtn}
                  onClick={() => move(i, "up")}
                  disabled={i === 0 || reordering || deleting}
                  title="تحريك لأعلى"
                >
                  <MdKeyboardArrowUp />
                </button>
                <button
                  className={styles.orderBtn}
                  onClick={() => move(i, "down")}
                  disabled={i === stories.length - 1 || reordering || deleting}
                  title="تحريك لأسفل"
                >
                  <MdKeyboardArrowDown />
                </button>
              </div>

              {/* Cover */}
              <div className={styles.thumb}>
                {story.images?.[0] ? (
                  <ProductImage
                    url={mediaUrl(story.images[0].imageUrl)}
                    alt={story.title}
                    className={styles.thumbImg}
                  />
                ) : (
                  <div className={styles.thumbPlaceholder}>
                    <FiImage />
                  </div>
                )}
              </div>

              {/* Title + meta */}
              <div className={storyStyles.info}>
                <span className={styles.catName}>{story.title}</span>
                <span className={storyStyles.meta}>
                  {[story.customerName, story.country].filter(Boolean).join(" · ")}
                  {story.images?.length ? ` · ${story.images.length} صور` : ""}
                </span>
              </div>

              {/* Order badge */}
              <span className={styles.orderBadge}>{i + 1}</span>

              {/* Actions */}
              <div className={styles.actions}>
                <button
                  className={styles.editBtn}
                  onClick={() => openEdit(story)}
                  disabled={deleting}
                  title="تعديل"
                >
                  <FaRegEdit />
                </button>
                <button
                  className={styles.deleteBtn}
                  onClick={() => !deleting && handleDelete(story)}
                  disabled={deleting}
                  title="حذف"
                >
                  {deleting ? <ImSpinner2 className="smallspinner" /> : <FaRegTrashAlt />}
                </button>
              </div>
            </div>
          ))}
        </div>
      )}

      <p className={styles.hint}>
        تظهر هذه القصص في قسم "قصص تصاميم خاصة" في الصفحة الرئيسية بنفس الترتيب هنا.
      </p>
    </div>
  );
};

export default StoriesPanel;
